import { forwardRef, useCallback, useId, useImperativeHandle, useState } from 'react'
import { Button } from '~/Components/Button'
import { Form, FormLabel } from '~/Components/Form.js'
import { Grid } from '~/Components/Grid'
import { Modal } from '~/Components/Modal'
import { TextInputFieldSimple } from '~/Components/TextInputField'
import { trpc, useMutationExt } from '~/Resources/TRPC'
import type { PagesStoreModel } from '~/Stores/PagesStore.js'

export interface EditPagePropertiesModalRef {
	show(pageNumber: number, pageInfo: PagesStoreModel | undefined): void
}
interface EditPagePropertiesModalProps {
	includeName: boolean
}

export const EditPagePropertiesModal = forwardRef<EditPagePropertiesModalRef, EditPagePropertiesModalProps>(
	function EditPagePropertiesModal({ includeName }, ref) {
		const [pageNumber, setPageNumber] = useState<number | null>(null)
		const [show, setShow] = useState(false)

		const [pageName, setName] = useState<string | null>(null)

		const setNameMutation = useMutationExt(trpc.pages.setName.mutationOptions())

		const doAction = useCallback(
			(e: React.FormEvent) => {
				e.preventDefault()
				e.stopPropagation()

				setShow(false)

				if (pageNumber === null) return

				setNameMutation
					.mutateAsync({
						pageNumber,
						name: pageName ?? '',
					})
					.catch((e) => {
						console.error('Failed to set name', e)
					})
			},
			[setNameMutation, pageNumber, pageName]
		)

		useImperativeHandle(
			ref,
			() => ({
				show(pageNumber, pageInfo) {
					setName(pageInfo?.name ?? null)
					setPageNumber(pageNumber)
					setShow(true)
				},
			}),
			[]
		)

		const onOpenChangeComplete = useCallback((open: boolean) => {
			if (!open) {
				setPageNumber(null)
				setName(null)
			}
		}, [])

		const nameFieldId = useId()

		return (
			<Modal.Root open={show} onOpenChange={setShow} onOpenChangeComplete={onOpenChangeComplete}>
				<Modal.Portal>
					<Modal.Backdrop />
					<Modal.Viewport>
						<Modal.Popup>
							<Modal.Header closeButton>
								<Modal.Title>Configure Page {pageNumber}</Modal.Title>
							</Modal.Header>
							<Modal.Body>
								<Form onSubmit={doAction}>
									{includeName && (
										<Grid.Row className="mb-4">
											<FormLabel htmlFor={nameFieldId} sm={3} column="sm">
												Name
											</FormLabel>
											<Grid.Col sm={9}>
												<TextInputFieldSimple
													id={nameFieldId}
													value={pageName || ''}
													setValue={setName}
													immediateValue
												/>
											</Grid.Col>
										</Grid.Row>
									)}
									{/* TODO: more fields should be added here */}
								</Form>
							</Modal.Body>
							<Modal.Footer>
								<Modal.Close>Cancel</Modal.Close>
								<Button color="primary" onClick={doAction}>
									Save
								</Button>
							</Modal.Footer>
						</Modal.Popup>
					</Modal.Viewport>
				</Modal.Portal>
			</Modal.Root>
		)
	}
)
